import * as DialogPrimitive from '@radix-ui/react-dialog'
import { ArrowLeft, Clock, CreditCard, Globe, Plus, Tag, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { MALLAR_PRICE_SEK } from '@/lib/mallar'

export interface TemplateMeta {
  id: string
  title: string
  description: string
  law_ref?: string
  icon?: string
  jurisdiction?: string
  category?: string
  estimated_time?: string
  preparations?: string[]
  important_notes?: string[]
  details?: string[]
}

interface Props {
  template: TemplateMeta | null
  open: boolean
  onClose: () => void
  onStart: (templateId: string) => void
  onBuy: (templateId: string) => void
  starting: boolean
  purchasing: boolean
  canCreate: boolean
  owned: boolean
}

export function TemplateDetailModal({ template, open, onClose, onStart, onBuy, starting, purchasing, canCreate, owned }: Props) {
  if (!template) return null

  return (
    <DialogPrimitive.Root open={open} onOpenChange={v => { if (!v) onClose() }}>
      <DialogPrimitive.Portal container={window.cl_shadowRootElement}>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-50 w-[calc(100vw-2rem)] max-w-2xl h-[90vh] translate-x-[-50%] translate-y-[-50%] bg-background border shadow-xl rounded-lg overflow-hidden flex flex-col duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">

          {/* Fixed top bar */}
          <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Visa alla
            </button>
            <DialogPrimitive.Close className="rounded-sm opacity-70 hover:opacity-100 transition-opacity">
              <X className="h-4 w-4" />
              <span className="sr-only">Stäng</span>
            </DialogPrimitive.Close>
          </div>

          {/* Scrollable content — all details here */}
          <ScrollArea className="flex-1 min-h-0">
            <div className="p-5 space-y-6">

              <h2 className="text-xl font-semibold">{template.title}</h2>

              {/* Description */}
              <div className="space-y-3">
                {template.details && template.details.length > 0
                  ? template.details.map((para, i) => (
                      <p key={i} className="text-sm text-muted-foreground leading-relaxed">{para}</p>
                    ))
                  : <p className="text-sm text-muted-foreground leading-relaxed">{template.description}</p>
                }
              </div>

              <Separator />

              {/* Metadata */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    <Globe className="h-3.5 w-3.5 shrink-0" />
                    Jurisdiktion
                  </div>
                  <p className="text-foreground">{template.jurisdiction ?? 'Sverige'}</p>
                </div>

                {template.category && (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      <Tag className="h-3.5 w-3.5 shrink-0" />
                      Kategori
                    </div>
                    <Badge variant="secondary" className="text-xs">{template.category}</Badge>
                  </div>
                )}

                {template.estimated_time && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      <Clock className="h-3.5 w-3.5 shrink-0" />
                      Beräknad tid
                    </div>
                    <p className="text-foreground">{template.estimated_time}</p>
                  </div>
                )}
              </div>

              {/* Preparations */}
              {template.preparations && template.preparations.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h3 className="text-base font-semibold">Förberedelser</h3>
                    <ul className="space-y-2">
                      {template.preparations.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-muted-foreground shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}

              {/* Important notes */}
              {template.important_notes && template.important_notes.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    <h3 className="text-base font-semibold">Viktiga noteringar</h3>
                    <ul className="space-y-2">
                      {template.important_notes.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-muted-foreground shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              )}

              {/* Legal references */}
              {template.law_ref && (
                <>
                  <Separator />
                  <div className="space-y-2">
                    <h3 className="text-base font-semibold">Rättsliga hänvisningar</h3>
                    <p className="text-sm text-muted-foreground">{template.law_ref}</p>
                  </div>
                </>
              )}

            </div>
          </ScrollArea>

          {/* Fixed bottom bar with CTA */}
          <div className="border-t p-4 shrink-0">
            {canCreate ? (
              <div className="flex flex-col gap-2">
                <Button
                  className="w-full gap-2"
                  onClick={() => onStart(template.id)}
                  disabled={starting}
                >
                  <Plus className="h-4 w-4" />
                  {starting ? 'Skapar...' : 'Skapa avtal'}
                </Button>
                {owned && (
                  <p className="text-center text-xs text-muted-foreground">
                    Du äger denna mall · skapa obegränsat antal avtal
                  </p>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Button
                  className="w-full gap-2"
                  onClick={() => onBuy(template.id)}
                  disabled={purchasing}
                >
                  <CreditCard className="h-4 w-4" />
                  {purchasing ? 'Öppnar kassan…' : `Köp mall – ${MALLAR_PRICE_SEK} kr`}
                </Button>
                <p className="text-center text-xs text-muted-foreground">
                  Engångsköp · skapa sedan obegränsat antal avtal · ingen prenumeration krävs
                </p>
              </div>
            )}
          </div>

        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
